<template>
  <v-app>
    <v-main>
      <form>
        <v-container grid-list-md class="ghost">
          <v-row class="justify-md-space-between justify-center">
            <v-col cols="10" sm="3">
              <v-text-field
                label="Patient ID / Name"
                outlined
                class="iol-v-text-field"
                ref="first"
                v-model="inputs.patientID"
              />
            </v-col>
            <v-col cols="10" sm="3">
              <v-select
                label="Keratometric Index"
                outlined
                class="smooth iol-v-select"
                v-model="inputs.keratometric_index"
                :items="$parent.keratometricItems"
                return-object
                required
              />
            </v-col>
            <v-col cols="10" sm="3">
              <v-select
                label="Biometer"
                :items="$parent.biometerItems"
                item-text="text"
                outlined
                class="smooth iol-v-select"
                v-model="inputs.biometer"
                return-object
                required
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-divider />
            </v-col>
          </v-row>
          <v-sheet class="iol-v-sheet">
            <v-row>
              <v-col cols="12">
                <v-btn
                  v-if="$vuetify.breakpoint.mobile"
                  @click="goToCamera"
                  color="#3B4256"
                  class="white--text"
                  style="width: 100%;"
                  x-large
                >
                  <v-icon class="mr-2">mdi-camera</v-icon>
                  Auto fill
                </v-btn>
              </v-col>
              <v-col cols="12" md="6">
                <v-sheet class="iol-right-v-sheet">
                  <h3>OD</h3>
                  <v-row class="justify-md-space-between">
                    <v-col cols="6" md="4">
                      <v-select
                        label="IOL Model"
                        :items="$parent.IOLModelItems"
                        item-text="text"
                        outlined
                        class="smooth iol-v-select"
                        v-model="inputs.iol_model"
                        return-object
                        @change="updateAConst(inputs)"
                      />
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-text-field
                        label="A constant"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.aconst"
                        :error-messages="getErrors('aconst', $v.inputs.aconst)"
                        :disabled="inputs.iol_model.type == '60MA'"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.aconst.$touch()"
                        @blur="$v.inputs.aconst.$touch()"
                        @change="$v.inputs.aconst.$touch()"
                      />
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-text-field
                        label="Target refraction (D)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.target"
                        :error-messages="getErrors('target', $v.inputs.target)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.target.$touch()"
                        @blur="$v.inputs.target.$touch()"
                        @change="$v.inputs.target.$touch()"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="justify-space-between">
                    <v-col cols="6" md="5">
                      <v-text-field
                        label="AL (mm)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.al"
                        :error-messages="getErrors('al', $v.inputs.al)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.al.$touch()"
                        @blur="$v.inputs.al.$touch()"
                        @change="$v.inputs.al.$touch()"
                      />
                      <v-text-field
                        label="K1 (D)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.k1"
                        :error-messages="getErrors('k1', $v.inputs.k1)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.k1.$touch()"
                        @blur="$v.inputs.k1.$touch()"
                        @change="$v.inputs.k1.$touch()"
                      />
                      <v-text-field
                        label="K2 (D)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.k2"
                        :error-messages="getErrors('k2', $v.inputs.k2)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.k2.$touch()"
                        @blur="$v.inputs.k2.$touch()"
                        @change="$v.inputs.k2.$touch()"
                      />
                      <v-text-field
                        label="ACD (mm)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.acd"
                        :error-messages="getErrors('acd', $v.inputs.acd)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.acd.$touch()"
                        @blur="$v.inputs.acd.$touch()"
                        @change="$v.inputs.acd.$touch()"
                      />
                    </v-col>
                    <v-col cols="6" md="6">
                      <v-text-field
                        label="Custom vitreous"
                        outlined
                        class="iol-v-text-field"
                        value="1.336"
                        readonly
                        disabled
                      />
                      <v-sheet class="iol-opt-v-sheet">
                        <h3>Optional</h3>
                        <br />
                        <v-text-field
                          label="LT (mm)"
                          outlined
                          class="iol-v-text-field"
                          v-model="inputs.lt"
                          :error-messages="getErrors('lt', $v.inputs.lt)"
                          type="number"
                          step="0.01"
                          @input="$v.inputs.lt.$touch()"
                          @blur="$v.inputs.lt.$touch()"
                        />
                        <v-text-field
                          label="CCT (µm)"
                          outlined
                          class="iol-v-text-field"
                          v-model="inputs.cct"
                          :error-messages="getErrors('cct', $v.inputs.cct)"
                          type="number"
                          step="0"
                          @input="$v.inputs.cct.$touch()"
                          @blur="$v.inputs.cct.$touch()"
                        />
                      </v-sheet>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
              <v-col cols="12" md="6">
                <v-sheet class="iol-left-v-sheet">
                  <h3>OS</h3>
                  <v-row class="justify-md-space-between">
                    <v-col cols="6" md="3">
                      <v-select
                        label="IOL Model"
                        :items="$parent.IOLModelItems"
                        item-text="text"
                        outlined
                        class="smooth iol-v-select"
                        v-model="inputs2.iol_model"
                        return-object
                        @change="updateAConst(inputs2)"
                      />
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-text-field
                        label="A constant"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs2.aconst"
                        :error-messages="getErrors('aconst', $v.inputs2.aconst)"
                        :disabled="inputs.iol_model.type == '60MA'"
                        type="number"
                        step="0.01"
                        @input="$v.inputs2.aconst.$touch()"
                        @blur="$v.inputs2.aconst.$touch()"
                        @change="$v.inputs2.aconst.$touch()"
                      />
                    </v-col>
                    <v-col cols="6" md="4">
                      <v-text-field
                        label="Target refraction (D)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs2.target"
                        :error-messages="getErrors('target', $v.inputs2.target)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs2.target.$touch()"
                        @blur="$v.inputs2.target.$touch()"
                        @change="$v.inputs2.target.$touch()"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="justify-space-between">
                    <v-col cols="6" md="5">
                      <v-text-field
                        label="AL (mm)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs2.al"
                        :error-messages="getErrors('al', $v.inputs2.al)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs2.al.$touch()"
                        @blur="$v.inputs2.al.$touch()"
                        @change="$v.inputs2.al.$touch()"
                      />
                      <v-text-field
                        label="K1 (D)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs2.k1"
                        :error-messages="getErrors('k1', $v.inputs2.k1)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs2.k1.$touch()"
                        @blur="$v.inputs2.k1.$touch()"
                        @change="$v.inputs2.k1.$touch()"
                      />
                      <v-text-field
                        label="K2 (D)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs2.k2"
                        :error-messages="getErrors('k2', $v.inputs2.k2)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs2.k2.$touch()"
                        @blur="$v.inputs2.k2.$touch()"
                        @change="$v.inputs2.k2.$touch()"
                      />
                      <v-text-field
                        label="ACD (mm)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs2.acd"
                        :error-messages="getErrors('acd', $v.inputs2.acd)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs2.acd.$touch()"
                        @blur="$v.inputs2.acd.$touch()"
                        @change="$v.inputs2.acd.$touch()"
                      />
                    </v-col>
                    <v-col cols="6" md="6">
                      <v-text-field
                        label="Custom vitreous"
                        outlined
                        class="iol-v-text-field"
                        value="1.336"
                        readonly
                        disabled
                      />
                      <v-sheet class="iol-opt-v-sheet">
                        <h3>Optional</h3>
                        <br />
                        <v-text-field
                          label="LT (mm)"
                          outlined
                          class="iol-v-text-field"
                          v-model="inputs2.lt"
                          :error-messages="getErrors('lt', $v.inputs2.lt)"
                          type="number"
                          step="0.01"
                          @input="$v.inputs2.lt.$touch()"
                          @blur="$v.inputs2.lt.$touch()"
                        />
                        <v-text-field
                          label="CCT (µm)"
                          outlined
                          class="iol-v-text-field"
                          v-model="inputs2.cct"
                          :error-messages="getErrors('cct', $v.inputs2.cct)"
                          type="number"
                          step="0"
                          @input="$v.inputs2.cct.$touch()"
                          @blur="$v.inputs2.cct.$touch()"
                        />
                      </v-sheet>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
            </v-row>
          </v-sheet>
          <v-row>
            <v-col cols="12">
              <v-divider />
              <br />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn @click="submit" color="#3B4256" class="white--text" x-large
                >Calculate</v-btn
              >
            </v-col>
          </v-row>
          <br />
          <v-row>
            <v-col cols="12" class="text-center">
              <a @click="clear" :style="$parent.resetstyle">Reset</a>
            </v-col>
          </v-row>
        </v-container>
      </form>
      <v-snackbar v-model="errorSnackbar.show" :color="errorSnackbar.color" top>
        {{ errorSnackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="errorSnackbar.show = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<style>
.v-application .primary--text {
  color: #3b4256 !important;
  caret-color: #3b4256 !important;
}
</style>

<script>
import { mapState, mapActions } from 'vuex'
import { validationMixin } from 'vuelidate'
import { requiredIf, minValue, between } from 'vuelidate/lib/validators'
import { solve, dualSolve, solveOS } from '@/graphql/queries/solve.gql'
import Logout from '@/vue/Logout.vue'
import { updateAConstExt } from '@/core/sync_methods.mjs'

let omitNull = (obj) => {
  let copy = { ...obj }
  Object.keys(copy)
    .filter((k) => copy[k] === null)
    .forEach((k) => delete copy[k])
  return copy
}

export default {
  mixins: [validationMixin],
  components: {
    Logout,
  },
  validations() {
    return {
      inputs: {
        al: {
          required: requiredIf(function(form) {
            return this.isRequired('OD')
          }),
          between: between(17, 40),
        },
        acd: {
          required: requiredIf(function(form) {
            return this.isRequired('OD')
          }),
          between: between(1, 6),
        },
        k1: {
          required: requiredIf(function(form) {
            return this.isRequired('OD')
          }),
          between: between(30, 60),
        },
        k2: {
          required: requiredIf(function(form) {
            return this.isRequired('OD')
          }),
          between: between(30, 60),
          minValue: function(value) {
            return (
              !value ||
              !this.inputs.k1 ||
              parseFloat(value) >= parseFloat(this.inputs.k1)
            )
          },
        },
        lt: { between: between(2.5, 8) },
        cct: { between: between(350, 700) },
        target: {
          required: requiredIf(function(form) {
            return this.isRequired('OD')
          }),
          between: between(-4, 1.5),
        },
        aconst: {
          required: requiredIf(function(form) {
            return (
              this.isRequired('OD') && this.inputs.iol_model.type != '60MA'
            )
          }),
          between: between(117, 121),
        },
      },
      inputs2: {
        al: {
          required: requiredIf(function(form) {
            return this.isRequired('OS')
          }),
          between: between(17, 40),
        },
        acd: {
          required: requiredIf(function(form) {
            return this.isRequired('OS')
          }),
          between: between(1, 6),
        },
        k1: {
          required: requiredIf(function(form) {
            return this.isRequired('OS')
          }),
          between: between(30, 60),
        },
        k2: {
          required: requiredIf(function(form) {
            return this.isRequired('OS')
          }),
          between: between(30, 60),
          minValue: function(value) {
            return (
              !value ||
              !this.inputs2.k1 ||
              parseFloat(value) >= parseFloat(this.inputs2.k1)
            )
          },
        },
        lt: { between: between(2.5, 8) },
        cct: { between: between(350, 700) },
        target: {
          required: requiredIf(function(form) {
            return this.isRequired('OS')
          }),
          between: between(-4, 1.5),
        },
        aconst: {
          required: requiredIf(function(form) {
            return (
              this.isRequired('OS') && this.inputs2.iol_model.type != '60MA'
            )
          }),
          between: between(117, 121),
        },
      },
    }
  },
  mounted() {
    if (this.$route.query.ocr) {
      this.$v.inputs.$touch()
      this.$v.inputs2.$touch()
    }
    this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs) }
    this.inputs2 = { ...omitNull(this.inputs2), ...omitNull(this.ginputs2) }
    this.inputs.side = true
    this.inputs2.side = false
    
    // Clean up properties from Complex/Second that don't exist in Regular
    delete this.inputs.is_non_physio_cornea
    delete this.inputs.is_icl
    delete this.inputs.is_rk
    delete this.inputs.is_corneal_laser
    delete this.inputs.corneal_laser_type
    delete this.inputs.laser_amount
    delete this.inputs.pre_lvc_arc
    delete this.inputs.prc_topo
    delete this.inputs.tk
    delete this.inputs.tcrp
    delete this.inputs.is_second
    delete this.inputs.al2
    delete this.inputs.k12
    delete this.inputs.k22
    delete this.inputs.acd2
    delete this.inputs.lt2
    delete this.inputs.cct2
    delete this.inputs.power2
    delete this.inputs.se2
    delete this.inputs.laser_amount2
    delete this.inputs.pre_lvc_arc2
    
    delete this.inputs2.is_non_physio_cornea
    delete this.inputs2.is_icl
    delete this.inputs2.is_rk
    delete this.inputs2.is_corneal_laser
    delete this.inputs2.corneal_laser_type
    delete this.inputs2.laser_amount
    delete this.inputs2.pre_lvc_arc
    delete this.inputs2.prc_topo
    delete this.inputs2.tk
    delete this.inputs2.tcrp
    delete this.inputs2.is_second
    delete this.inputs2.al2
    delete this.inputs2.k12
    delete this.inputs2.k22
    delete this.inputs2.acd2
    delete this.inputs2.lt2
    delete this.inputs2.cct2
    delete this.inputs2.power2
    delete this.inputs2.se2
    delete this.inputs2.laser_amount2
    delete this.inputs2.pre_lvc_arc2
  },
  data: () => {
    let ret = {
      inputs: {
        patientID: '',
        keratometric_index: 1.3375,
        biometer: { type: 'Other', text: 'Other' },
        side: true,
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
        target: 0,
        al: '',
        k1: '',
        k2: '',
        acd: '',
        lt: '',
        cct: '',
      },
      inputs2: {
        patientID: '',
        keratometric_index: 1.3375,
        biometer: { type: 'Other', text: 'Other' },
        side: false,
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
        target: 0,
        al: '',
        k1: '',
        k2: '',
        acd: '',
        lt: '',
        cct: '',
      },
      inputs_default: {
        patientID: '',
        keratometric_index: 1.3375,
        biometer: { type: 'Other', text: 'Other' },
        side: true,
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
        target: 0,
        al: '',
        k1: '',
        k2: '',
        acd: '',
        lt: '',
        cct: '',
      },
      errorSnackbar: {
        show: false,
        text: '',
        color: 'error',
      },
    }
    if (process.env.NODE_ENV == 'development') {
      ret.inputs = {
        ...ret.inputs,
        patientID: 'testid',
        keratometric_index: 1.3375,
        biometer: { type: 'Other', text: 'Other' },
        al: 22.52,
        acd: 3.24,
        k1: 43.87,
        k2: 46.14,
        lt: 4.83,
        cct: 487,
        target: 0.5,
        iol_model: {
          type: 'PCB',
          text: 'PCB',
          aconst: 119.04
        },
        aconst: 119.04,
      }
      ret.inputs2 = {
        ...ret.inputs2,
        al: 22,
        acd: 4,
        k1: 33,
        k2: 34,
        lt: 4,
        cct: 530,
        target: 1,
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
      }
    }
    return ret
  },

  computed: {
    ...mapState(['appHeader', 'ginputs', 'ginputs2']),
  },

  watch: {
    inputs: {
      handler: function(newValue) {
        // Auto-save to ginputs when inputs change
        this.$store.dispatch('updateInputs', newValue)
      },
      deep: true,
    },
    inputs2: {
      handler: function(newValue) {
        // Auto-save to ginputs2 when inputs2 change
        this.$store.dispatch('updateInputs2', newValue)
      },
      deep: true,
    },
  },

  methods: {
    getErrors(name, model) {
      const errors = []
      if (!model.$dirty) return errors
      switch (name) {
        case 'k2':
          !model.minValue && errors.push('K2 must be greater than K1')
        case 'al':
        case 'acd':
        case 'k1':
        case 'k2':
        case 'target':
        case 'aconst':
          !model.required && errors.push('required')
          !model.between && errors.push('value out of bounds')
          break
        case 'lt':
        case 'cct':
          model !== '' && !model.between && errors.push('value out of bounds')
          break
        default:
          break
      }
      return errors
    },
    isEmpty(model) {
      return model === '' || model === null
    },
    isRequired(side) {
      if (side === 'OD') {
        return (
          !this.isEmpty(this.inputs.al) ||
          !this.isEmpty(this.inputs.k1) ||
          !this.isEmpty(this.inputs.k2) ||
          !this.isEmpty(this.inputs.acd)
        )
      } else if (side === 'OS') {
        return (
          !this.isEmpty(this.inputs2.al) ||
          !this.isEmpty(this.inputs2.k1) ||
          !this.isEmpty(this.inputs2.k2) ||
          !this.isEmpty(this.inputs2.acd)
        )
      } else {
        return false
      }
    },
    updateAConst(input) {
      updateAConstExt(this.$parent, input)
    },
    focusFirstError(component = this) {
      if (component.hasError) {
        component.focus()
        return true
      }

      let focused = false

      component.$children.some((childComponent) => {
        focused = this.focusFirstError(childComponent)
        return focused
      })

      return focused
    },
    submit() {
      this.$v.inputs.$touch()
      this.$v.inputs2.$touch()
      this.$nextTick(() => {
          if (this.focusFirstError()) {
          return
        }
        Object.keys(this.inputs).forEach(
          (k) =>
            (this.inputs[k] = this.inputs[k] === '' ? null : this.inputs[k]),
        )
        Object.keys(this.inputs2).forEach(
          (k) =>
            (this.inputs2[k] = this.inputs2[k] === '' ? null : this.inputs2[k]),
        )

        // copy needed var in inputs2 for calculation
        this.inputs2['patientID'] = this.inputs['patientID']
        this.inputs2['biometer'] = this.inputs['biometer']
        this.inputs2['keratometric_index'] = this.inputs['keratometric_index']

        // update store
        const inputs = { ...omitNull(this.inputs) }
        const inputs2 = { ...omitNull(this.inputs2) }
        this.$store.dispatch('updateInputs', this.inputs)
        this.$store.dispatch('updateInputs2', this.inputs2)

        // send type to get_result
        inputs['biometer'] = inputs['biometer'].type
        inputs2['biometer'] = inputs2['biometer'].type
        if (inputs['iol_model']) {
          inputs['iol_model'] = inputs['iol_model'].type
        }
        if (inputs2['iol_model']) {
          inputs2['iol_model'] = inputs2['iol_model'].type
        }

        // delete those inputs because not used in calculation, so not sent via graphql,
        // only stored in ginputs for reuse on other tab
        delete inputs['patientID']
        delete inputs2['patientID']

        if (this.isRequired('OD') && this.isRequired('OS')) {
          this.$apollo
            .query({
              query: dualSolve,
              variables: {
                inputs,
                inputs2,
              },
            })
            .then((result) => {
              this.$store.dispatch('setResults', result.data.solveOD)
              this.$store.dispatch('setResults2', result.data.solveOS)
              this.$store.dispatch('updateLastCalc', 'regular_both')
              this.$router.push('result_regular')
            })
            .catch((e) => {
              this.$raven.captureException(e)
              this.errorSnackbar.text =
                e.message || 'An error occurred during calculation'
              this.errorSnackbar.show = true
            })
        } else if (this.isRequired('OD')) {
          this.$apollo
            .query({
              query: solve,
              variables: {
                inputs,
              },
            })
            .then((result) => {
              this.$store.dispatch('setResults', result.data.solve)
              this.$store.dispatch('updateLastCalc', 'regular_od')
              this.$router.push('result_regular')
            })
            .catch((e) => {
              this.$raven.captureException(e)
              this.errorSnackbar.text =
                e.message || 'An error occurred during calculation'
              this.errorSnackbar.show = true
              console.log(e)
            })
        } else if (this.isRequired('OS')) {
          this.$apollo
            .query({
              query: solveOS,
              variables: {
                inputs2,
              },
            })
            .then((result) => {
              this.$store.dispatch('setResults2', result.data.solve)
              this.$store.dispatch('updateLastCalc', 'regular_os')
              this.$router.push('result_regular')
            })
            .catch((e) => {
              this.$raven.captureException(e)
              this.errorSnackbar.text =
                e.message || 'An error occurred during calculation'
              this.errorSnackbar.show = true
            })
        }
      })
    },
    clear() {
      Object.keys(this.inputs).forEach(
        (k) => (this.inputs[k] = this.inputs_default[k]),
      )
      Object.keys(this.inputs2).forEach(
        (k) => (this.inputs2[k] = this.inputs_default[k]),
      )
      this.inputs2.side = false
      // Reset store
      this.$store.dispatch('resetInputs')
      this.$store.dispatch('resetInputs2')
      this.$refs.first.focus()
    },
    goToCamera() {
      this.$router.push({ name: 'CameraCapture' })
    },
    ...mapActions([
      'updateInputs',
      'setResults',
      'updateInputs2',
      'setResults2',
      'setAuthUser',
      'resetInputs',
      'resetInputs2',
    ]),
  },
}
</script>
